import request from "@/lib/api/handler";
import { toast } from "sonner";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";
import { CompanyUserAccountType, TeamMember } from "@/queries/company/team";
import { AdminCompanyDetails } from "@/queries/admin/company-details";

interface AddTeamMemberParams {
  name: string;
  email: string;
  accountType: CompanyUserAccountType;
}

export const useAddTeamMember = (companyId: string, onSuccess?: () => void) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (body: AddTeamMemberParams[]) => {
      const token = await getAccessTokenSilently();

      return Promise.all(
        body.map((data) =>
          request<TeamMember>({
            method: "POST",
            path: `/api/company/${companyId}/team`,
            body: JSON.stringify(data),
            token,
          }),
        ),
      );
    },
    onSettled: (_, error) => {
      if (error) {
        toast.error("Error adding team member, please try again");
      } else {
        toast.success("Team member added!");
        onSuccess?.();
      }
    },
    onSuccess: (data) => {
      // Check for an existing companyTeam entry
      const prevCompanyTeam = qc.getQueryData<TeamMember[]>(
        QueryKeys.companyTeam(companyId),
      );

      if (prevCompanyTeam) {
        // If there is an existing entry, update it
        qc.setQueryData<TeamMember[]>(QueryKeys.companyTeam(companyId), [
          ...prevCompanyTeam,
          ...data,
        ]);
      }

      // Same as above, but for company details
      const prevCompanyDetails = qc.getQueryData<AdminCompanyDetails>(
        QueryKeys.adminCompany(companyId),
      );

      if (prevCompanyDetails) {
        qc.setQueryData<AdminCompanyDetails>(
          QueryKeys.adminCompany(companyId),
          {
            ...prevCompanyDetails,
            team: [...prevCompanyDetails.team, ...data],
          },
        );
      }
    },
  });

  return {
    addTeamMember: mutate,
    isAddingTeamMember: isPending,
    teamMemberError: error,
  };
};
